import { useCallback, useEffect } from 'react';
import { InsuletStatusCode } from 'Enums';
import { ApiLoginWarning } from 'Constants/index';
import { redirect } from 'Utilities/browser';
import mediator from 'Utilities/mediator';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { signIn } from 'Services/auth';
import { verifyCid } from 'Services/insulet';
import useAuth from '../../useAuth';
import useInsuletError from './useInsuletError';
const useInsuletAuth = () => {
  const {
    redirectOnError
  } = useInsuletError();
  const onDisplayLockoutMessage = useCallback(() => {
    setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.LOCKOUT_ERROR);
    }, 5000);
  }, [redirectOnError]);
  const {
    handleLoginError,
    handleLockoutError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage,
    navigateToPhoneVerification
  } = useAuth(onDisplayLockoutMessage);
  useEffect(() => {
    const sessionExpiredInterval = setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.INTERNAL_ERROR);
    }, 600000);
    return () => {
      clearTimeout(sessionExpiredInterval);
    };
  }, [redirectOnError]);
  const redirectUserAfterAuth = useCallback(async ({
    accessToken,
    accountId,
    cid,
    country,
    redirect_uri
  }) => {
    const redirectUrl = await verifyCid(cid, country, redirect_uri, accountId, accessToken);
    redirect(redirectUrl);
  }, []);
  const authenticateUser = useCallback(async (email, password, redirectUser = true) => {
    const {
      cid,
      country,
      redirect_uri,
      app
    } = store.getState().env;
    const {
      accessToken,
      accountId,
      expires,
      verificationTasks
    } = await signIn(email, password, app);
    persistAuthSession({
      accessToken,
      accountId,
      expires,
      cid,
      app,
      country
    });
    const unverifiedPhoneTask = verificationTasks?.find(task => task.verified === false && task.action === ApiLoginWarning.REGISTER_ACTION && task.verificationMethod === ApiLoginWarning.PHONE_VERIFICATION);
    if (unverifiedPhoneTask) {
      return navigateToPhoneVerification(unverifiedPhoneTask, async () => {
        const {
          accessToken,
          accountId,
          expires
        } = await signIn(email, password, app);
        persistAuthSession({
          accessToken,
          accountId,
          expires,
          cid,
          app,
          country
        });
        if (redirectUser) {
          redirectUserAfterAuth({
            cid,
            country,
            redirect_uri,
            accountId,
            accessToken
          });
        }
      });
    }
    if (redirectUser) {
      redirectUserAfterAuth({
        cid,
        country,
        redirect_uri,
        accountId,
        accessToken
      });
    }
  }, [navigateToPhoneVerification, redirectUserAfterAuth]);
  const handleCountryRegionError = useCallback(() => {
    const redirectUrl = store.getState().env.redirect_uri;
    redirectOnError(redirectUrl, InsuletStatusCode.WRONG_REGION);
  }, [redirectOnError]);
  const handleForgotPasswordDone = useCallback(() => {
    const redirectUrl = store.getState().env.redirect_uri;
    redirectOnError(redirectUrl, InsuletStatusCode.FORGOT_PASSWORD);
  }, [redirectOnError]);
  const handleResetPasswordDone = useCallback(() => {
    const partners = store.getState().config.partners;
    const pId = store.getState().env.pId;
    const website = partners ? partners[pId]?.website : '';
    if (website) {
      redirect(website);
    }
  }, []);
  const handleUnexpectedError = useCallback(() => {
    mediator.publish('router:navigate', '/auth-error');
    setTimeout(() => {
      const redirectUrl = store.getState().env.redirect_uri;
      redirectOnError(redirectUrl, InsuletStatusCode.INTERNAL_ERROR);
    }, 5000);
  }, [redirectOnError]);
  return {
    authenticateUser,
    handleLoginError,
    handleLockoutError,
    handleCountryRegionError,
    handleForgotPasswordDone,
    handleResetPasswordDone,
    handleUnexpectedError,
    showHcpError,
    showForgotPasswordBlockedMessage,
    showForgotPasswordConfirmatioMessage,
    showTokenExpirationError,
    showCountryRegionError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage
  };
};
export default useInsuletAuth;